import { HTMLProps, useEffect, useMemo, useRef, useState } from "react";

import { DateTime } from "luxon";

import { useQueryClient } from "@tanstack/react-query";
import { ChevronRightIcon } from "@heroicons/react/24/outline";
import { ChatBubbleBottomCenterIcon, MapPinIcon, TruckIcon } from "@heroicons/react/24/solid";
import { createColumnHelper, RowModel } from "@tanstack/react-table";

import OrderStatus from "./OrderStatus";
import Modal from "../../layouts/Modal";
import { IPart } from "../../models/Part";
import PartImage from "../stock/PartImage";
import OrderSaleSource from "./OrderSaleSource";
import LoadingWheel from "../shared/LoadingWheel";
import { PageMode } from "../../models/PagedTable";
import openInNewTab from "../../utils/openInNewTab";
import EbayPaymentStatus from "./EbayPaymentStatus";
import { SaleSource } from "../../models/EbayOffer";
import PagedTable from "../shared/Tables/PagedTable";
import usePickingList from "../../data/usePickingList";
import useShippingBulk from "../../data/useShippingBulk";
import formatDateTime from './../../utils/formatDateTime';
import TrackingInfoModal from "../stock/TrackingInfoModal";
import PartInvoiceExpandedRow from "./PartInvoiceExpandedRow";
import useNotification from "../notifications/useNotifications";
import useShippingPolicies from "../../data/useShippingPolicies";
import PinProtect, { PinProtectState } from "../shared/PinProtect";
import CreateShippingLabelsModal from "./CreateShippingLabelsModal";
import { ManageShippingModal } from "../stock/shipping/ManageShippingModal";
import WarehouseLocationFromId from "../warehouseLocations/WarehouseLocationFromId";
import { useForm } from "react-hook-form";
import SelectInput from "../shared/Inputs/SelectInput";
import { useDebounce } from "@uidotdev/usehooks";
import ComboBoxInput from "../shared/Inputs/ComboBoxInput";


function IndeterminateCheckbox({
  indeterminate,
  className = '',
  ...rest
}: { indeterminate?: boolean } & HTMLProps<HTMLInputElement>) {
  const ref = useRef<HTMLInputElement>(null!)

  useEffect(() => {
    if (typeof indeterminate === 'boolean') {
      ref.current.indeterminate = !rest.checked && indeterminate
    }
  }, [ref, indeterminate])

  return (
    <input
      type="checkbox"
      ref={ref}
      className={className + 'p-3 cursor-pointer'}
      {...rest}
    />
  )
}

const CourierPill = ({ courier }: { courier: string }) => {
  let color = "bg-gray-100 text-gray-800";

  switch (courier) {
    case "FedEx":
      color = "bg-purple-100 text-purple-800";
      break;
    case "Proovia":
      color = "bg-blue-100 text-blue-800";
      break;
    case "Parcelforce":
      color = "bg-yellow-100 text-yellow-800";
      break;
    case "RoyalMail":
      color = "bg-red-100 text-red-800";
      break;
    case "Other":
      color = "bg-green-100 text-green-800";
      break;
  }

  return (
    <div className={`inline-block px-2 py-1 rounded-full text-xs font-semibold mt-2 ${color}`}>
      {courier}
    </div>
  );

}

const actionsClass = "h-5 w-5 text-gray-500 hover:text-primary hover:scale-125 cursor-pointer transition duration-200";

const colHelper = createColumnHelper<IPart>();


export default function EbayOrdersTable() {
  const queryClient = useQueryClient();
  const { register, watch, setValue } = useForm();

  const [additionalBody, setAdditionalBody] = useState<Record<string, any>>({});

  const { shippingPolicies } = useShippingPolicies();

  const { bulkShipping } = useShippingBulk();
  const { addNotification } = useNotification();

  const { downloadPickingList, isDownloading } = usePickingList();

  const [selectedPart, setSelectedPart] = useState<IPart | null>(null);
  const [customShippingOpen, setCustomShippingOpen] = useState(false);

  const [selectedRows, setSelectedRows] = useState<IPart[]>([]);
  const [shippingLabelsModalOpen, setShippingLabelsModalOpen] = useState(false);

  const [pinModalOpen, setPinModalOpen] = useState(false);
  const [pinState, setPinState] = useState<PinProtectState>();

  const [shippingModalOpen, setShippingModalOpen] = useState(false);
  const [buyerNotesOpen, setBuyerNotesOpen] = useState(false);

  // Debounce the additionalBody to prevent rapid successive queries
  const debouncedAdditionalBody = useDebounce(additionalBody, 500);

  const cols = useMemo(
    () => [
    {
      id: 'select',
      header: ({ table }: any) => (
        <IndeterminateCheckbox
          {...{
            checked: table.getIsAllRowsSelected(),
            indeterminate: table.getIsSomeRowsSelected(),
            onChange: table.getToggleAllRowsSelectedHandler(),
          }}
        />
      ),
      cell: ({ row }: any) => (
        <div className="flex justify-center items-center flex-1 p-4" onClick={(e) => e.stopPropagation()}>
          <IndeterminateCheckbox
            {...{
              id: row.original.id,
              checked: row.getIsSelected(),
              disabled: !row.getCanSelect(),
              indeterminate: row.getIsSomeSelected(),
              onChange: row.getToggleSelectedHandler(),
            }}
          />
          <button
            onClick={(e) => { e.stopPropagation(); row.toggleExpanded(); }}
            style={{ transform: `rotate(${!row.getIsExpanded() ? "0" : "-270"}deg)` }}
            className="ml-3 p-1 transition duration-500 cursor-pointer border border-gray-400 rounded-full hover:bg-gray-200"
          >
            {/* {row.getIsExpanded() ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4"/>} */}
            <ChevronRightIcon className="h-4 w-4" />
          </button>
        </div>
      ),
    },
    colHelper.display({
      id: "images",
      cell: (info) => <PartImage part={info.row.original} />
    }),
    colHelper.accessor("title", {
      header: "Category",
      sortingFn: "auto",
      // cell: (info) => <span>{info.getValue() !== null ? date.format(new Date(info.getValue()!), "DD/MM/YYYY") : "Not Set"}</span>,
    }),
    colHelper.accessor("offers", {
      id: "offer title",
      header: "Offer Title",
      sortingFn: "auto",
      cell: (info) => <span>{info.getValue()?.at(0)?.title}</span>,
    }),
    colHelper.accessor("soldDate", {
      header: "Sold Date",
      sortingFn: "auto",
      cell: (info) => <span>{formatDateTime(info.getValue(), DateTime.DATETIME_SHORT)}</span>,
    }),
    colHelper.accessor("pickedOn", {
      header: "Picked Date",
      sortingFn: "auto",
      cell: (info) => <span>{formatDateTime(info.getValue(), DateTime.DATETIME_SHORT)}</span>,
    }),
    colHelper.accessor("shippedOn", {
      header: "Shipped Date",
      sortingFn: "auto",
      cell: (info) => <span>{formatDateTime(info.getValue(), DateTime.DATETIME_SHORT)}</span>,
    }),
    colHelper.accessor("tagNumber", {
      header: "Tag No.",
      sortingFn: "auto",
    }),
    colHelper.accessor("warehouseLocationId", {
      header: "Warehouse",
      sortingFn: "auto",
      cell: (info) => <WarehouseLocationFromId id={info.getValue()} lastStockChecked={info.row.original.lastStockChecked} />,
    }),
    colHelper.display({
      header: "Shipping",
      cell: (info) => {
        let part = info.row.original;
        if (part.shippingPolicyId != null && shippingPolicies.data?.data != null) {
          let policy = shippingPolicies.data.data.find(p => p.id === part.shippingPolicyId);
          if (policy != null) {
            return <div>{policy.name} <CourierPill courier={policy?.courier ?? ""} /></div>;
          }
        }
        return <div className="italic text-gray-400">Not Set</div>;
      },
    }),
    colHelper.accessor("offers", {
      id: "payment status",
      header: "Payment Status",
      enableSorting: false,
      cell: (info) => <EbayPaymentStatus offers={info.getValue()} />,
    }),
    colHelper.accessor("offers", {
      id: "payment method",
      header: "Sale Source",
      enableSorting: false,
      cell: (info) => <OrderSaleSource offers={info.getValue()} />,
    }),
    colHelper.accessor("offers", {
      id: "offer status",
      header: "Status",
      enableSorting: false,
      cell: (info) => <OrderStatus part={info.row.original} />,
    }),
    colHelper.display({
      id: "actions",
      header: "Actions",
      cell: (info) => (
        <div className="flex gap-2 items-center justify-center">
          <MapPinIcon className={`${actionsClass} ${info.row.original.shippedOn != null ? "pointer-events-none" : ""}`} title="Tracking Info" onClick={(e) => {
            e.stopPropagation();
            setSelectedPart(info.row.original);
            setCustomShippingOpen(true);
          }} />
          <TruckIcon className={`${actionsClass} ${info.row.original.shippingLabelDocumentId == null ? "!text-red-400" : "!text-green-400"}`} title="Shipping Label" onClick={(e) => {
            e.stopPropagation();
            setSelectedPart(info.row.original);
            setShippingModalOpen(true);
          }} />
          <ChatBubbleBottomCenterIcon className={`${actionsClass} ${info.row.original.offers?.[0].buyerCheckoutNotes ? "!text-green-400" : "pointer-events-none"}`} title="Messages" onClick={(e) => {
            e.stopPropagation();
            setSelectedPart(info.row.original);
            setBuyerNotesOpen(true);
          }} />
        </div>
      ),
      enableSorting: false,
    }),
  ],
    []
  );


  const shipAll = (rows: RowModel<IPart>) => {
    let selected = rows.rows.filter(row => row.getIsSelected()).map(r => r.original.id)

    if (selected.length === 0) {
      addNotification({
        variant: "warn",
        primaryText: "No Items Selected",
      });
      return;
    }

    bulkShipping.mutate(selected, {
      onSuccess: () => {
        addNotification({
          variant: "success",
          primaryText: "Items Marked As Shipped",
        });
      }
    });
  }

  const downloadShippingList = async (rows: RowModel<IPart>) => {
    const selectedRows = rows.rows.filter(r => r.getIsSelected());
    if (selectedRows.length === 0) {
      addNotification({ variant: "warn", primaryText: "No Items Selected" });
      return;
    }

    // Only "Not Picked" -> no pickedOn and no shippedOn
    const eligible = selectedRows
      .filter(r => !r.original.pickedOn && !r.original.shippedOn)
      .map(r => r.original.id);

    const skipped = selectedRows.length - eligible.length;

    if (eligible.length === 0) {
      addNotification({
        variant: "warn",
        primaryText: "No 'Not Picked' items in your selection",
        secondaryText: "Picked or shipped items were skipped.",
      });
      return;
    }

    try {
      if (skipped > 0) {
        addNotification({
          variant: "info",
          primaryText: `Skipped ${skipped} picked/shipped item${skipped > 1 ? "s" : ""}`,
        });
      }

      await downloadPickingList(eligible);
      addNotification({ variant: "success", primaryText: "Picking list downloaded" });
    } catch (err: any) {
      console.error(err);
      addNotification({
        variant: "error",
        primaryText: "Failed to generate picking list",
        secondaryText: err?.message ?? "Unknown error",
      });
    }
  }

  const openCreateShippingLabelModal = (rows: RowModel<IPart>) => {
    setSelectedRows(rows.rows.filter(row => row.getIsSelected()).map(r => r.original));
    setShippingLabelsModalOpen(true);
  }


  const watchShippingPolicyId = watch("shippingPolicyId");

  useEffect(() => {
    setAdditionalBody({
      shippingPolicyId: watchShippingPolicyId?.id ?? null,
    });
  }, [watchShippingPolicyId]);

  if (shippingPolicies.isLoading) {
    return <LoadingWheel />;
  }

  return (
    <>
      <CreateShippingLabelsModal
        isOpen={shippingLabelsModalOpen}
        onClose={() => {
          queryClient.invalidateQueries(["pagedTable"]);
          setShippingLabelsModalOpen(false);
        }}
        parts={selectedRows}
      />

      <PinProtect
        open={pinModalOpen}
        setOpen={setPinModalOpen}
        pinState={pinState}
      />

      <Modal open={buyerNotesOpen} setOpen={setBuyerNotesOpen} width="max-w-2xl mx-4">
        <div className="p-4">
          <h3 className="text-lg font-semibold text-primary-600">Buyer Checkout Notes</h3>
          <div className="mt-2">
            {selectedPart?.offers?.[0].buyerCheckoutNotes || "No notes available"}
          </div>
        </div>
      </Modal>

      {selectedPart && (
        <>
          <TrackingInfoModal
            isOpen={customShippingOpen}
            onClose={() => setCustomShippingOpen(false)}
            customLoading={false}
            setCustomLoading={() => { }}
            part={selectedPart}
            setPinModalOpen={setPinModalOpen}
            setPinState={setPinState}
          />
          <Modal
            open={shippingModalOpen}
            setOpen={setShippingModalOpen}
            width="max-w-4xl mx-4"
          >
            <ManageShippingModal part={selectedPart} setOpen={setShippingModalOpen} />
          </Modal>
        </>
      )}

      <form className="flex gap-x-6 mt-4 mb-2">

        <div className="flex w-full max-w-64">
          <ComboBoxInput
            label={"Shipping Policy"}
            register={register}
            registerName={"shippingPolicyId"}
            options={shippingPolicies.data?.data ?? []}
            setValue={setValue}
            containerClasses="w-full min-w-[30vw]"
            placeholder="Start Typing to Search Shipping Policies"
            searchField="name"
          // disabled={isSearchDisabled}
          />
        </div>
      </form>

      <PagedTable
        url="/part/listings"
        pageMode={PageMode.Orders}
        columns={cols}
        queryLabel="Search order details"
        additionalBody={debouncedAdditionalBody}
        onRowClick={x => openInNewTab(`stock/${x.id}`)}
        modeToggles={[
          { label: "Ready To Pick", pageMode: PageMode.OrdersPaid },
          { label: "Picked", pageMode: PageMode.OrdersPicked },
          { label: "Shipped", pageMode: PageMode.OrdersShipped }]}
        actionButtons={[
          { label: "Mark as Shipped", disabledOnNoRows: true, onClick: shipAll, isLoading: bulkShipping.isLoading },
          { label: isDownloading ? "Generating..." : "Download Pick List", disabledOnNoRows: true, onClick: downloadShippingList, isLoading: isDownloading },
          { label: "Create Shipping Labels", disabledOnNoRows: true, onClick: openCreateShippingLabelModal },
        ]}
        showTotal
        rowColor={(part) => {
          let colour = "";

          let offer = part.offers?.at(0);

          if (offer?.saleSource === SaleSource.Counter) {
            colour = "bg-green-50 hover:bg-green-100"
          }

          if (offer?.fulfillmentPolicyId != null && shippingPolicies.data?.data != null) {
            let policy = shippingPolicies.data.data.find(p => p.policyId === offer?.fulfillmentPolicyId);
            if (policy != null && part.soldDate != null) {
              let handlingTime = policy.handlingTime;
              // get due date from solddate string
              const soldDate = new Date(part.soldDate);
              let dueDate = DateTime.fromJSDate(soldDate).plus({ hours: handlingTime });
              let now = DateTime.now();
              if (part.shippedOn == null) {
                if (now > dueDate) {
                  colour = "bg-red-50 hover:bg-red-100";
                }
              }
            }
          }

          return colour;
        }}
        otherInfo={[{ label: "Total Overdue", value: "overdue" }]}
        expandedRow={(part) => (
          <PartInvoiceExpandedRow part={part} />
        )}
      />
    </>
  );
}
